import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { Observable } from 'rxjs';

import { EVENT_ORGANISER_ROLE, EventOrganiserApplication } from './models';
import { OrganiserApplicationService } from './organiser-application.service';
import { UserService } from '../global/userService/user-service';

@Component({
    standalone: false,
    selector: 'abs-organiser-application',
    template: `
        <abs-page-layout [pageTitle]="'GLOBAL.EVENTS.ORGANISER_APPLICATION.TITLE' | translate">
            @if (loading()) {
                <ion-spinner name="crescent"></ion-spinner>
            } @else if (application()) {
                <ion-card [color]="statusColor()">
                    <ion-card-content>
                        @switch (application()!.status) {
                            @case ('pending') {
                                <p>{{ 'GLOBAL.EVENTS.ORGANISER_APPLICATION.PENDING' | translate }}</p>
                            }
                            @case ('approved') {
                                @if (hasOrganiserRole()) {
                                    <p>{{ 'GLOBAL.EVENTS.ORGANISER_APPLICATION.APPROVED' | translate }}</p>
                                } @else {
                                    <p>{{ 'GLOBAL.EVENTS.ORGANISER_APPLICATION.APPROVAL_NO_LONGER_ACTIVE' | translate }}</p>
                                }
                            }
                            @case ('rejected') {
                                <p>{{ 'GLOBAL.EVENTS.ORGANISER_APPLICATION.REJECTED' | translate }}</p>
                                @if (application()!.reviewNotes) {
                                    <p class="review-notes">{{ application()!.reviewNotes }}</p>
                                }
                            }
                        }
                    </ion-card-content>
                </ion-card>
                @if (application()!.status === 'rejected' && !showForm()) {
                    <ion-button expand="block" (click)="showForm.set(true)">
                        {{ 'GLOBAL.EVENTS.ORGANISER_APPLICATION.REAPPLY' | translate }}
                    </ion-button>
                }
            }

            @if (showApplicationForm()) {
                <form>
                    <ion-item>
                        <ion-input
                            [label]="'GLOBAL.EVENTS.ORGANISER_APPLICATION.CONTACT_NAME' | translate"
                            labelPlacement="stacked"
                            [(ngModel)]="contactName"
                            name="contactName"
                        ></ion-input>
                    </ion-item>
                    <ion-item>
                        <ion-input
                            [label]="'GLOBAL.EVENTS.ORGANISER_APPLICATION.CONTACT_EMAIL' | translate"
                            labelPlacement="stacked"
                            [(ngModel)]="contactEmail"
                            name="contactEmail"
                            type="email"
                        ></ion-input>
                    </ion-item>
                    <ion-item>
                        <ion-checkbox [(ngModel)]="wantsPaidEvents" name="wantsPaidEvents" justify="start">
                            {{ 'GLOBAL.EVENTS.ORGANISER_APPLICATION.WANTS_PAID_EVENTS' | translate }}
                        </ion-checkbox>
                    </ion-item>
                    <p class="paid-note">{{ 'GLOBAL.EVENTS.ORGANISER_APPLICATION.WANTS_PAID_EVENTS_NOTE' | translate }}</p>
                    @if (submitError()) {
                        <p class="submit-error">{{ 'GLOBAL.EVENTS.ORGANISER_APPLICATION.SUBMIT_ERROR' | translate }}</p>
                    }
                    <ion-button expand="block" [disabled]="submitting() || !contactName || !contactEmail" (click)="submit()">
                        {{ 'GLOBAL.EVENTS.ORGANISER_APPLICATION.SUBMIT' | translate }}
                    </ion-button>
                </form>
            }
        </abs-page-layout>
    `,
    // Styles live in army-builder-shared/src/lib/global/styles/events.scss, per the
    // workspace rule against CSS in component files.
})
export class OrganiserApplicationPage implements OnInit {
    private service = inject(OrganiserApplicationService);
    private userService = inject(UserService);

    application = signal<EventOrganiserApplication | null>(null);
    loading = signal(true);
    submitting = signal(false);
    showForm = signal(false);
    submitError = signal(false);

    /**
     * The granted role is what actually confers organiser access — the application
     * record is only the request for it. A role can be revoked without touching the
     * application, so these two can disagree and the role wins.
     */
    hasOrganiserRole = computed(() => (this.userService.user()?.roles || []).includes(EVENT_ORGANISER_ROLE));

    /**
     * Show the form whenever there is something left to apply for: the user doesn't
     * hold the role, and no application is already awaiting review. Without this, an
     * approved-but-revoked user saw only an "approved" card and had no way to ask
     * again — and no admin could help, because the review queue lists pending
     * applications only.
     */
    showApplicationForm = computed(() => {
        if (this.hasOrganiserRole()) {
            return false;
        }
        const status = this.application()?.status;
        if (status === 'pending') {
            return false;
        }
        return status !== 'rejected' || this.showForm();
    });

    statusColor = computed(() => {
        switch (this.application()?.status) {
            case 'approved':
                return this.hasOrganiserRole() ? 'success' : 'warning';
            case 'rejected':
                return 'danger';
            default:
                return 'warning';
        }
    });

    contactName = '';
    contactEmail = '';
    wantsPaidEvents = false;

    ngOnInit() {
        const user = this.userService.user();
        this.contactName = user?.displayName || '';
        this.contactEmail = user?.email || '';

        this.service.getMine().subscribe({
            next: async (app) => {
                this.application.set(app);

                // Session roles are baked into the access token at login, so they lag
                // behind a grant until the token is refreshed. Refresh before deciding
                // anything, so an approval this session hasn't caught up with isn't
                // mistaken for one that was revoked.
                if (app?.status === 'approved' && !this.hasOrganiserRole()) {
                    await this.userService.refreshAccessToken();
                }

                this.loading.set(false);
            },
            error: () => this.loading.set(false)
        });
    }

    submit() {
        this.submitting.set(true);
        this.submitError.set(false);
        this.service
            .apply({
                contactName: this.contactName,
                contactEmail: this.contactEmail,
                wantsPaidEvents: this.wantsPaidEvents
            })
            .subscribe({
                next: (app) => {
                    this.application.set(app);
                    this.showForm.set(false);
                    this.submitting.set(false);
                },
                error: () => {
                    // Say so. A submission that silently does nothing leaves the user
                    // pressing the button again with no idea why nothing happened.
                    this.submitError.set(true);
                    this.submitting.set(false);
                }
            });
    }
}
